import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import { categoryService } from '../../../services/categoryService';
import { manufacturerService } from '../../../services/manufacturerService';

const FilterSidebar = ({ filters, onFilterChange, onClearFilters, isOpen, onToggle, viewMode, onRequestProductClick }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    category: true,
    price: false,
    brand: true
  });
  const [categories, setCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [categoryError, setCategoryError] = useState('');
  
  const [manufacturers, setManufacturers] = useState([]);
  const [isLoadingManufacturers, setIsLoadingManufacturers] = useState(false);

  // Add state for price range inputs
  const [priceMin, setPriceMin] = useState(filters?.priceRange?.min || '');
  const [priceMax, setPriceMax] = useState(filters?.priceRange?.max || '');

  // Keep price inputs in sync with filters prop
  useEffect(() => {
    if (filters?.priceRange?.min !== undefined) setPriceMin(filters.priceRange.min);
    if (filters?.priceRange?.max !== undefined) setPriceMax(filters.priceRange.max);
    if (!filters?.priceRange) {
      setPriceMin('');
      setPriceMax('');
    }
  }, [filters?.priceRange]);

  // Load categories and manufacturers from database
  useEffect(() => {
    loadCategories();
    loadManufacturers();
  }, []);

  const loadCategories = async () => {
    try {
      setIsLoadingCategories(true);
      setCategoryError('');
      
      const fetchedCategories = await categoryService?.getAllWithCounts();
      const transformedCategories = fetchedCategories?.map(cat => ({
        id: cat?.id,
        label: cat?.name,
        count: cat?.productCount || 0
      }));
      
      setCategories(transformedCategories || []);
    } catch (err) {
      console.error('Failed to load categories:', err);
      setCategoryError(err?.message || 'Failed to load categories');
      setCategories([]);
    } finally {
      setIsLoadingCategories(false);
    }
  };

  const loadManufacturers = async () => {
    try {
      setIsLoadingManufacturers(true);
      const data = await manufacturerService?.getAll();
      setManufacturers(data || []);
    } catch (err) {
      console.error('Failed to load manufacturers:', err);
    } finally {
      setIsLoadingManufacturers(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev?.[section]
    }));
  };

  const handleFilterChange = (filterType, value, checked) => {
    onFilterChange(filterType, value, checked);
  };

  // Add handler for price range application
  const handleApplyPriceRange = () => {
    const minPrice = priceMin ? parseFloat(priceMin) : null;
    const maxPrice = priceMax ? parseFloat(priceMax) : null;
    
    // Validate price range
    if (minPrice !== null && maxPrice !== null && minPrice > maxPrice) {
      alert('Minimum price cannot be greater than maximum price');
      return;
    }
    
    // Apply price range filter
    onFilterChange('priceRange', { min: minPrice, max: maxPrice }, true);
  };

  // Add handler to remove price range filter
  const handleRemovePriceRange = () => {
    setPriceMin('');
    setPriceMax('');
    onFilterChange('priceRange', null, false);
  };

  // Helper function to get display names
  const getFilterDisplayValue = (key, value) => {
    if (key === 'categories') {
      const category = categories?.find(cat => cat?.id === value);
      return category?.label || value;
    } else if (key === 'manufacturers') {
      const manufacturer = manufacturers?.find(m => m?.id === value);
      return manufacturer?.name || value;
    } else if (key === 'priceRange' && value) {
      const parts = [];
      if (value?.min !== null && value?.min !== undefined) parts?.push(`$${value?.min}`);
      if (value?.max !== null && value?.max !== undefined) parts?.push(`$${value?.max}`);
      return parts?.join(' - ') || 'Price Range';
    }
    return value;
  };

  // Filter manufacturers based on selected categories
  const selectedCategoryIds = filters?.categories || [];
  const filteredBrands = manufacturers
    ?.filter(m => {
      if (selectedCategoryIds?.includes(m?.categoryId)) return true;
      // Symmetrical keyword safety: if Category is Motorcycles, also include motorcycle brands!
      const isMotorcycleCatSelected = selectedCategoryIds.includes('f73124d6-3a3d-40a4-96ec-c478b5e92129');
      if (isMotorcycleCatSelected) {
        const motorBrands = ['harley', 'ducati', 'vespa', 'piaggio', 'ktm', 'yamaha', 'kawasaki'];
        return motorBrands.some(b => m?.name?.toLowerCase().includes(b));
      }
      return false;
    })
    ?.map(m => ({
      id: m?.id,
      label: m?.name,
      count: 0 // Placeholder
    }));

  const FilterSection = ({ title, items, filterKey, isExpanded, onToggle, isLoading }) => {
    const [localSearch, setLocalSearch] = useState('');
    const showSearch = items?.length > 25;
    
    const displayedItems = showSearch && localSearch 
      ? items.filter(item => item.label.toLowerCase().includes(localSearch.toLowerCase()))
      : items;

    return (
      <div className="border-b border-border pb-4 mb-4">
        <button
          onClick={onToggle}
          className="flex items-center justify-between w-full text-left font-medium text-foreground hover:text-primary transition-colors"
        >
          <span>{title}</span>
          <Icon 
            name={isExpanded ? "ChevronUp" : "ChevronDown"} 
            size={16} 
          />
        </button>
        
        {isExpanded && (
          <div className="mt-3">
            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : items?.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">No {title?.toLowerCase()} found</p>
            ) : (
              <>
                {showSearch && (
                  <div className="mb-3">
                    <Input 
                      type="text" 
                      placeholder={`Search ${title.toLowerCase()}...`}
                      value={localSearch}
                      onChange={(e) => setLocalSearch(e.target.value)}
                      className="h-8 text-sm"
                      unwrapped={false}
                    />
                  </div>
                )}
                <div className={`space-y-2 ${showSearch ? 'max-h-60 overflow-y-auto pr-2 custom-scrollbar' : ''}`}>
                  {displayedItems?.map((item) => (
                    <div key={item?.id} className="flex items-center justify-between">
                      <Checkbox
                        label={item?.label}
                        checked={filters?.[filterKey]?.includes(item?.id) || false}
                        onChange={(e) => handleFilterChange(filterKey, item?.id, e?.target?.checked)}
                        className="flex-1"
                      />
                      {item?.count > 0 && (
                        <span className="text-xs text-muted-foreground ml-2">
                          {item?.count?.toLocaleString()}
                        </span>
                      )}
                    </div>
                  ))}
                  {showSearch && displayedItems.length === 0 && (
                    <p className="text-xs text-muted-foreground italic">No matches found</p>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onToggle}
        />
      )}
      {/* Sidebar */}
      <div className={`
        fixed lg:sticky top-16 left-0 h-screen lg:h-auto w-80 bg-card border-r border-border 
        transform transition-transform duration-300 z-50 lg:z-auto lg:transform-none
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-6 h-full overflow-y-auto">
          {/* Symmetrical Request Product Button */}
          <div className="mb-6 p-4 rounded-xl bg-primary/5 border border-primary/10 shadow-inner text-left">
            <h4 className="text-xs font-black uppercase text-primary tracking-wider mb-2">Can't find a model?</h4>
            <Button 
              type="button" 
              variant="default" 
              size="sm" 
              className="w-full text-xs font-black uppercase tracking-widest bg-blue-600 hover:bg-blue-700 text-white shadow-md py-2.5 px-4 rounded-lg flex items-center justify-center gap-1.5 transition-all border border-blue-700"
              onClick={(e) => {
                console.log('🎯 [FilterSidebar] Request New Product Button Clicked!');
                if (onRequestProductClick) {
                  onRequestProductClick();
                } else {
                  console.warn('⚠️ [FilterSidebar] onRequestProductClick prop is undefined!');
                }
              }}
            >
              <Icon name="PlusCircle" size={12} className="mr-1.5" /> Request New Product
            </Button>
          </div>

          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-foreground">Filters</h2>
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearFilters}
                className="text-muted-foreground hover:text-foreground"
              >
                Clear All
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={onToggle}
                className="lg:hidden"
              >
                <Icon name="X" size={20} />
              </Button>
            </div>
          </div>

          {/* Search within filters */}
          <div className="mb-6">
            <Input
              type="text"
              placeholder="Search filters..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full"
            />
          </div>

          {/* Filter Sections */}
          <div className="space-y-0">
            <FilterSection
              title="Category"
              items={categories?.filter(cat => 
                cat?.label?.toLowerCase()?.includes(searchTerm?.toLowerCase())
              )}
              filterKey="categories"
              isExpanded={expandedSections?.category}
              onToggle={() => toggleSection('category')}
              isLoading={isLoadingCategories}
            />

            {/* Brand Section - Only shown when category is selected */}
            {selectedCategoryIds?.length > 0 && (
              <FilterSection
                title="Brand"
                items={filteredBrands?.filter(brand => 
                  brand?.label?.toLowerCase()?.includes(searchTerm?.toLowerCase())
                )}
                filterKey="manufacturers"
                isExpanded={expandedSections?.brand}
                onToggle={() => toggleSection('brand')}
                isLoading={isLoadingManufacturers}
              />
            )}

            {/* Price Range */}
            <div className="border-b border-border pb-4 mb-4">
              <button
                onClick={() => toggleSection('price')}
                className="flex items-center justify-between w-full text-left font-medium text-foreground hover:text-primary transition-colors"
              >
                <span>Price Range</span>
                <Icon 
                  name={expandedSections?.price ? "ChevronUp" : "ChevronDown"} 
                  size={16} 
                />
              </button>
              
              {expandedSections?.price && (
                <div className="mt-3 space-y-3">
                  <div className="flex items-center space-x-2">
                    <Input
                      type="number"
                      placeholder="Min"
                      value={priceMin === null ? '' : priceMin}
                      onChange={(e) => setPriceMin(e?.target?.value)}
                      className="flex-1 min-w-0"
                      min="0"
                      step="0.01"
                    />
                    <span className="text-muted-foreground px-1 shrink-0">to</span>
                    <Input
                      type="number"
                      placeholder="Max"
                      value={priceMax === null ? '' : priceMax}
                      onChange={(e) => setPriceMax(e?.target?.value)}
                      className="flex-1 min-w-0"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1 min-w-0"
                      onClick={handleApplyPriceRange}
                      disabled={!priceMin && !priceMax}
                    >
                      Apply
                    </Button>
                    {(priceMin || priceMax) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="shrink-0"
                        onClick={handleRemovePriceRange}
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </div>
              )}            </div>
          </div>

          {/* Active Filters */}
          <div className="mt-6 pt-6 border-t border-border">
            <h3 className="text-sm font-medium text-foreground mb-3">Active Filters</h3>
            <div className="flex flex-wrap gap-2">
              {Object.entries(filters)?.map(([key, values]) => {
                // Handle array values (categories, manufacturers)
                if (Array.isArray(values)) {
                  return values?.map(value => (
                    <div
                      key={`${key}-${value}`}
                      className="flex items-center bg-primary text-primary-foreground px-3 py-1.5 rounded-md text-xs font-medium shadow-sm"
                    >
                      <span className="mr-1.5">{getFilterDisplayValue(key, value)}</span>
                      <button
                        onClick={() => handleFilterChange(key, value, false)}
                        className="hover:opacity-80 transition-opacity"
                        aria-label="Remove filter"
                      >
                        <Icon name="X" size={12} />
                      </button>
                    </div>
                  ));
                }
                // Handle priceRange object
                else if (key === 'priceRange' && values) {
                  return (
                    <div
                      key={key}
                      className="flex items-center bg-primary text-primary-foreground px-3 py-1.5 rounded-md text-xs font-medium shadow-sm"
                    >
                      <span className="mr-1.5">{getFilterDisplayValue(key, values)}</span>
                      <button
                        onClick={handleRemovePriceRange}
                        className="hover:opacity-80 transition-opacity"
                        aria-label="Remove price range filter"
                      >
                        <Icon name="X" size={12} />
                      </button>
                    </div>
                  );
                }
                return null;
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FilterSidebar;